import React, { useEffect, useState } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { AusweisInfo, getMe, ZuepStatusInfo } from './ApiClient';

function fmtDate(d?: string | null): string {
  if (!d) return '';
  const [y, m, day] = d.split('-');
  return `${day}.${m}.${y}`;
}

function daysUntil(d?: string | null): number | null {
  if (!d) return null;
  const t = new Date(`${d}T00:00:00`).getTime();
  return Math.ceil((t - Date.now()) / 86_400_000);
}

const baseStyle: React.CSSProperties = {
  maxWidth: 640,
  margin: '4px auto 0',
  padding: '10px 18px',
  borderRadius: 10,
  textAlign: 'center',
  fontSize: 15,
  fontWeight: 500,
  lineHeight: 1.35,
};

const VARIANTS: Record<string, React.CSSProperties> = {
  green: { background: '#e7f5ec', color: '#1e7e34', border: '1px solid #b7e1c1' },
  orange: { background: '#fff4e5', color: '#b35900', border: '1px solid #ffd8a8' },
  red: { background: '#fdecec', color: '#c0392b', border: '1px solid #f5c2c0' },
  blue: { background: '#e7f0fb', color: '#1c5fb0', border: '1px solid #b8d3f0' },
  grey: { background: '#f1f3f5', color: '#495057', border: '1px solid #dee2e6' },
};

const SEVERITY_VARIANT: Record<string, keyof typeof VARIANTS> = {
  ok: 'green',
  pending: 'blue',
  warn: 'orange',
  error: 'red',
  none: 'grey',
};

const CODE_ICON: Record<string, string> = {
  zuverlaessig: '✅',
  in_bearbeitung: '🔄',
  angehalten: '⏸️',
  unzuverlaessig: '🔴',
  aufgehoben: '🔴',
  offen: '⚠️',
  keine: '⚪',
};

type AblaufBannerProps = {
  /** Optional vorgeladen (z.B. von ModeSelection, das /api/me ohnehin für
   * das Kachel-Gating holt) — dann macht das Banner KEINEN eigenen Fetch. */
  zuepStatus?: ZuepStatusInfo | null;
  state?: 'loading' | 'ok' | 'none' | 'error';
  /** Flughafenausweis-Daten (Ausweisstelle) — zweite Banner-Zeile mit Stand. */
  ausweisInfo?: AusweisInfo | null;
};

/** Zweite Banner-Zeile: Flughafenausweis-Gültigkeit inkl. Datenstand. */
function AusweisZeile({ info }: { info: AusweisInfo }) {
  if (!info.gueltig_bis) return null;
  const dleft = daysUntil(info.gueltig_bis);
  const variant =
    dleft != null && dleft <= 30 ? 'red' : dleft != null && dleft <= 90 ? 'orange' : 'green';
  return (
    <div style={{ ...baseStyle, ...VARIANTS[variant], marginTop: 6 }}>
      🪪 Flughafenausweis – gültig bis <strong>{fmtDate(info.gueltig_bis)}</strong>
      {dleft != null && (dleft >= 0 ? ` (noch ${dleft} Tage)` : ` (seit ${-dleft} Tagen abgelaufen)`)}
      <div style={{ fontSize: 12, fontWeight: 400, opacity: 0.75 }}>
        Daten der Ausweisstelle — Stand {fmtDate(info.stand)}, nicht tagesaktuell
      </div>
    </div>
  );
}

/**
 * Zeigt oben im Portal den ZÜP-Status des eingeloggten Mitglieds:
 * nicht nur das Ablaufdatum, sondern auch "in Bearbeitung / angehalten /
 * aufgehoben / Unterlagen unvollständig / keine". Daten aus dem Gateway
 * (`GET /api/me -> zuep_status`, abgeleitet in app/member_status.py).
 * Zeigt nichts, solange geladen wird oder bei Fehler; bei 404 (kein
 * verknüpfter Datensatz) einen dezenten Hinweis.
 */
function AblaufBanner({ zuepStatus, state: extState, ausweisInfo }: AblaufBannerProps) {
  const { keycloak } = useKeycloak();
  const controlled = extState !== undefined;
  const [ownZs, setOwnZs] = useState<ZuepStatusInfo | null>(null);
  const [ownState, setOwnState] = useState<'loading' | 'ok' | 'none' | 'error'>('loading');
  const zs = controlled ? zuepStatus ?? null : ownZs;
  const state = controlled ? extState : ownState;

  useEffect(() => {
    if (controlled) return;
    let cancelled = false;
    getMe(keycloak.token)
      .then((me) => {
        if (!cancelled) {
          setOwnZs(me.zuep_status ?? null);
          setOwnState('ok');
        }
      })
      .catch((err: { status?: number }) => {
        if (!cancelled) setOwnState(err?.status === 404 ? 'none' : 'error');
      });
    return () => {
      cancelled = true;
    };
  }, [keycloak.token, controlled]);

  if (state === 'loading' || state === 'error') return null;
  if (state === 'none' || !zs) {
    return (
      <div style={{ ...baseStyle, ...VARIANTS.grey }}>
        Kein verknüpfter ZÜP-Datensatz gefunden.
      </div>
    );
  }

  // Zuverlässig: Restlaufzeit + Ampel (grün / orange <=90 / rot <=30 bzw. abgelaufen).
  if (zs.code === 'zuverlaessig' && zs.frist) {
    const dleft = daysUntil(zs.frist);
    const variant =
      dleft != null && dleft <= 30 ? 'red' : dleft != null && dleft <= 90 ? 'orange' : 'green';
    const frist = zs.frist;
    return (
      <>
      <div style={{ ...baseStyle, ...VARIANTS[variant] }}>
        ✅ Zuverlässig – gültig bis <strong>{fmtDate(frist)}</strong>
        {dleft != null && (dleft >= 0 ? ` (noch ${dleft} Tage)` : ` (seit ${-dleft} Tagen abgelaufen)`)}
      </div>
      {ausweisInfo && <AusweisZeile info={ausweisInfo} />}
      </>
    );
  }

  // Alle übrigen Status: Label + passende Farbe (+ ggf. Frist / offene Punkte).
  const variant = SEVERITY_VARIANT[zs.severity] || 'grey';
  return (
    <>
    <div style={{ ...baseStyle, ...VARIANTS[variant] }}>
      {CODE_ICON[zs.code] || ''} <strong>{zs.label}</strong>
      {zs.code === 'in_bearbeitung' && zs.frist && ` (aktuell gültig bis ${fmtDate(zs.frist)})`}
      {zs.open && zs.open.length > 0 && (
        <div style={{ fontSize: 13, fontWeight: 400, marginTop: 4 }}>{zs.open.join(' · ')}</div>
      )}
    </div>
    {ausweisInfo && <AusweisZeile info={ausweisInfo} />}
    </>
  );
}

export default AblaufBanner;
